import { useCallback, useEffect, useRef } from 'react';
import { api, ensureCsrfCookie } from '@/services/api';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import type { Tenant, User } from '@/types';

interface MeResponse {
    user: User;
    tenant: Tenant | null;
    impersonating: boolean;
    impersonator_name: string | null;
}

export function useAuth() {
    const { user, tenant, isLoading, impersonating, impersonatorName, setSession, setLoading, clear } = useAuthStore();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get<MeResponse>('/me');
            setSession(data.user, data.tenant, data.impersonating, data.impersonator_name);
            // The sidebar's nav is cached under a session-agnostic ['nav'] key with a 5-minute
            // staleTime — without this, logging in as a different user/tenant (or returning from
            // impersonation) kept showing whichever menu was cached until a manual page reload
            // wiped the in-memory query cache. Every session change must invalidate it.
            //
            // This imports the shared queryClient singleton directly rather than calling the
            // useQueryClient() hook: useAuth() is invoked via useAuthBootstrap() at the very top
            // of App(), before that component's own <QueryClientProvider> has mounted around it,
            // so the hook throws ("No QueryClient set") at that call site.
            void queryClient.invalidateQueries({ queryKey: ['nav'] });
        } catch {
            clear();
        }
    }, [setSession, setLoading, clear]);

    const login = useCallback(
        async (email: string, password: string) => {
            await ensureCsrfCookie();
            await api.post('/auth/login', { email, password });
            await refresh();
        },
        [refresh],
    );

    const logout = useCallback(async () => {
        await api.post('/auth/logout');
        clear();
        void queryClient.invalidateQueries({ queryKey: ['nav'] });
    }, [clear]);

    const stopImpersonating = useCallback(async () => {
        await api.post('/impersonate/stop');
        await refresh();
    }, [refresh]);

    return {
        user,
        tenant,
        isLoading,
        isAuthenticated: !!user,
        impersonating,
        impersonatorName,
        login,
        logout,
        refresh,
        clear,
        stopImpersonating,
    };
}

/**
 * Bootstraps the session exactly once for the whole app. `useAuth()` is called from many
 * components (layouts, topbar, pages) so the mount-time `/me` fetch lives here instead of
 * inside `useAuth()` itself — otherwise every consumer fires its own duplicate request.
 */
export function useAuthBootstrap() {
    const { refresh, clear } = useAuth();
    const didRun = useRef(false);

    useEffect(() => {
        // In dev, StrictMode mounts effects twice to surface missing cleanup — without this
        // guard that fires two concurrent unauthenticated /me requests, each capable of minting
        // its own session, which can race with the CSRF cookie and cause a spurious 419 on login.
        if (!didRun.current) {
            didRun.current = true;
            void refresh();
        }

        const onUnauthenticated = () => clear();
        window.addEventListener('aipa:unauthenticated', onUnauthenticated);
        return () => window.removeEventListener('aipa:unauthenticated', onUnauthenticated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
